import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, Chip, IconButton, Alert, AlertTitle,
  CircularProgress, Autocomplete, TextField, MenuItem, Divider, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, useTheme, alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  AccountTree as ChainIcon,
} from '@mui/icons-material';
import grapeRulesService, {
  RegionGrapeRules, RulePeriod, RuleMode, RuleWidensConflict, SweepResult, EffectiveRule,
} from '../../services/grapeRulesService';
import grapeService, { Grape } from '../../services/grapeService';

/**
 * GK-1 §2 — the curator's view of what a region permits.
 *
 * Three things this panel is careful about:
 *   1. it SEEDS NOTHING. A region with no rules shows "not recorded" and
 *      stays that way until a curator writes one — the default is a true
 *      statement, not a stub waiting to be filled;
 *   2. the INHERITANCE CHAIN is always visible, because a curator cannot
 *      sensibly narrow a rule they cannot see;
 *   3. the write-time refusal (§2.4) is shown as the server phrased it,
 *      with the offending grapes named. It is the only "no" this feature
 *      gives a human, and it must read like an explanation.
 */

const COLOURS = ['RED', 'WHITE', 'ROSE', 'ORANGE', 'SPARKLING', 'DESSERT', 'FORTIFIED'];

const MODE_HELP: Record<RuleMode, string> = {
  exclusive: 'The list is complete and closed — only these. This is the only mode that can contradict a bottle.',
  restricted: 'A restriction exists and these are the ones we have recorded. The list may be incomplete, so it never contradicts.',
  open: 'No varietal restriction in law (many IGP / Vin de France).',
  unknown: 'We have not recorded this law. Nothing is confirmed, and nothing is accused.',
};

const MODE_COLOUR: Record<RuleMode, 'error' | 'warning' | 'success' | 'default'> = {
  exclusive: 'success', restricted: 'warning', open: 'default', unknown: 'default',
};

const emptyPeriod = (): RulePeriod => ({ effectiveFrom: null, effectiveTo: null, byColour: {} });

const windowLabel = (p: RulePeriod): string => {
  if (p.effectiveFrom == null && p.effectiveTo == null) return 'all vintages';
  if (p.effectiveFrom == null) return `up to ${p.effectiveTo}`;
  if (p.effectiveTo == null) return `${p.effectiveFrom} onwards`;
  return `${p.effectiveFrom}–${p.effectiveTo}`;
};

interface Props {
  regionId: string;
  regionName: string;
}

const RegionGrapeRulesPanel: React.FC<Props> = ({ regionId, regionName }) => {
  const theme = useTheme();
  const [data, setData] = useState<RegionGrapeRules | null>(null);
  const [periods, setPeriods] = useState<RulePeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [conflicts, setConflicts] = useState<RuleWidensConflict[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sweep, setSweep] = useState<SweepResult | null>(null);
  const [grapeOptions, setGrapeOptions] = useState<Grape[]>([]);
  const [grapeNames, setGrapeNames] = useState<Record<string, string>>({});
  /** '' means "inherit" — distinct from 0, which would be a real (absurd) threshold. */
  const [minimumInput, setMinimumInput] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await grapeRulesService.getRegionRules(regionId);
      setData(res);
      setPeriods(res.periods.length > 0 ? res.periods : []);
      setMinimumInput(res.varietalMinimum?.own != null ? String(res.varietalMinimum.own) : '');
      setGrapeNames((prev) => ({ ...prev, ...res.grapeNames }));
      setDirty(false);
      setError(null);
    } catch {
      setError('Could not load the grape rules for this region.');
    } finally {
      setLoading(false);
    }
  }, [regionId]);

  useEffect(() => { load(); }, [load]);

  const searchGrapes = useCallback(async (q: string) => {
    if (q.trim().length < 2) return;
    try {
      const res = await grapeService.getAllGrapes(1, 20, 'primaryName', 'asc', q.trim());
      setGrapeOptions(res.data);
      setGrapeNames((prev) => ({
        ...prev, ...Object.fromEntries(res.data.map((g) => [g._id, g.primaryName])),
      }));
    } catch { /* the picker simply shows nothing */ }
  }, []);

  const nameOf = useCallback((id: string) => grapeNames[id] ?? id, [grapeNames]);

  const mutate = (fn: (draft: RulePeriod[]) => void) => {
    setPeriods((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as RulePeriod[];
      fn(next);
      return next;
    });
    setDirty(true);
    setConflicts(null);
    setSweep(null);
  };

  const setColourRule = (pIdx: number, colour: string, patch: Partial<{ mode: RuleMode; grapes: string[] }>) => {
    mutate((draft) => {
      const existing = draft[pIdx].byColour[colour] ?? { mode: 'unknown' as RuleMode, grapes: [] };
      draft[pIdx].byColour[colour] = { ...existing, ...patch };
    });
  };

  const save = async () => {
    setSaving(true);
    setConflicts(null);
    setError(null);
    try {
      const res = await grapeRulesService.putRegionRules(
        regionId,
        periods,
        // Blank means inherit, so it is sent as null rather than omitted —
        // omitting would leave a stale override in place.
        minimumInput.trim() === '' ? null : Number(minimumInput),
      );
      setSweep(res.sweep);
      await load();
    } catch (e: any) {
      const body = e?.response?.data;
      if (body?.code === 'RULE_WIDENS_ANCESTOR') {
        setConflicts(body.conflicts);
      } else {
        setError(body?.detail ?? body?.message ?? 'Could not save these rules.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Colours worth showing in the effective view: the ones anybody has an
  // opinion about, so an unrecorded region does not render seven "unknown"s.
  const effectiveShown = useMemo<EffectiveRule[]>(
    () => (data?.effectiveByColour ?? []).filter((e) => e.mode !== 'unknown'),
    [data],
  );

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Permitted grapes</Typography>
        <Button
          variant="contained" size="small" startIcon={<SaveIcon />}
          disabled={!dirty || saving} onClick={save}
        >
          {saving ? 'Saving…' : 'Save rules'}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What the law allows in {regionName}, by colour and by vintage. Rules are inherited
        from parent regions and may only be narrowed here, never widened.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* §2.4 — the refusal, in the words the server chose. */}
      {conflicts && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>This rule would permit what a parent region forbids</AlertTitle>
          {conflicts.map((c, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>{c.sentence}</Typography>
          ))}
          <Typography variant="caption" color="text.secondary">
            Nothing was saved. One of the two records is wrong — this one, or the parent's.
          </Typography>
        </Alert>
      )}

      {sweep && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSweep(null)}>
          Saved. Re-checked {sweep.wineNamesChecked} wine name{sweep.wineNamesChecked === 1 ? '' : 's'} across{' '}
          {sweep.regionsInSubtree} region{sweep.regionsInSubtree === 1 ? '' : 's'}:{' '}
          {sweep.created} new finding{sweep.created === 1 ? '' : 's'}, {sweep.reopened} reopened,{' '}
          {sweep.resolved} no longer firing.
        </Alert>
      )}

      {/* ── the inheritance chain ─────────────────────────────────────────── */}
      <Box sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.06), borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ChainIcon fontSize="small" color="info" />
          <Typography variant="subtitle2">What applies here, after inheritance</Typography>
        </Box>

        {effectiveShown.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing recorded — not here, and not on any parent region. No wine in {regionName} can be
            contradicted on its grapes until somebody records what this appellation permits.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Colour</TableCell>
                <TableCell>Rule</TableCell>
                <TableCell>Grapes</TableCell>
                <TableCell>Set by</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {effectiveShown.map((e) => (
                <TableRow key={e.colour}>
                  <TableCell>{e.colour}</TableCell>
                  <TableCell>
                    <Tooltip title={MODE_HELP[e.mode]}>
                      <Chip size="small" label={e.mode} color={MODE_COLOUR[e.mode]} variant="outlined" />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {e.grapes.length === 0
                      ? <Typography variant="caption" color="text.secondary">—</Typography>
                      : e.grapes.map((g) => (
                        <Chip key={g} size="small" label={nameOf(g)} sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {e.via.filter((v) => v.mode !== 'unknown').map((v) => v.regionName).join(' → ') || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* ── §2.5 the varietal labelling minimum ───────────────────────────── */}
      <Box sx={{ mb: 3, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Varietal labelling minimum
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How much of a grape a wine must contain before its name may appear alone on the label.
          This is a jurisdiction rule — usually set once on a country or trade bloc, and overridden
          only where a region is stricter.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small" type="number" label="Set here (%)" sx={{ width: 170 }}
            value={minimumInput}
            onChange={(e) => { setMinimumInput(e.target.value); setDirty(true); setConflicts(null); setSweep(null); }}
            helperText="blank = inherit"
            inputProps={{ min: 1, max: 100 }}
          />
          {data?.varietalMinimum?.effectivePct != null ? (
            <Typography variant="body2" color="text.secondary">
              {data.varietalMinimum.inherited
                ? `Currently inheriting ${data.varietalMinimum.effectivePct}% from ${data.varietalMinimum.setBy}.`
                : `${data.varietalMinimum.effectivePct}% is set on ${regionName} itself.`}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No threshold is set here or on any parent region, so the single-varietal check
              cannot run for wines here.
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── this region's own periods ─────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">
          Rules recorded on {regionName} itself
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => mutate((d) => d.push(emptyPeriod()))}>
          Add a period
        </Button>
      </Box>

      {periods.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          None. {data?.ancestors.length ? 'This region inherits from its parents.' : ''} Add a period only
          when you have a source for what this appellation permits — an empty record is the correct
          statement of what we know, and costs nothing to maintain.
        </Typography>
      )}

      {periods.map((p, pIdx) => (
        <Box key={pIdx} sx={{ mb: 2, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small" label="From vintage" type="number" sx={{ width: 140 }}
              value={p.effectiveFrom ?? ''}
              onChange={(ev) => mutate((d) => {
                d[pIdx].effectiveFrom = ev.target.value === '' ? null : Number(ev.target.value);
              })}
              helperText="blank = always"
            />
            <TextField
              size="small" label="To vintage" type="number" sx={{ width: 140 }}
              value={p.effectiveTo ?? ''}
              onChange={(ev) => mutate((d) => {
                d[pIdx].effectiveTo = ev.target.value === '' ? null : Number(ev.target.value);
              })}
              helperText="blank = still current"
            />
            <Chip size="small" label={windowLabel(p)} />
            <Box sx={{ flexGrow: 1 }} />
            <IconButton size="small" onClick={() => mutate((d) => d.splice(pIdx, 1))} aria-label="Remove period">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>

          <TextField
            size="small" fullWidth label="Source (regulation, decision, or page)" sx={{ mb: 2 }}
            value={p.source?.ref ?? ''}
            onChange={(ev) => mutate((d) => {
              d[pIdx].source = { ...(d[pIdx].source ?? {}), kind: 'regulation', ref: ev.target.value };
            })}
          />

          <Divider sx={{ mb: 2 }} />

          {COLOURS.map((colour) => {
            const rule = p.byColour[colour] ?? { mode: 'unknown' as RuleMode, grapes: [] };
            const listed = rule.mode === 'exclusive' || rule.mode === 'restricted';
            return (
              <Box key={colour} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ width: 96, pt: 1.5 }}>{colour}</Typography>
                <TextField
                  select size="small" sx={{ width: 160 }} value={rule.mode}
                  onChange={(ev) => setColourRule(pIdx, colour, { mode: ev.target.value as RuleMode })}
                >
                  {(Object.keys(MODE_HELP) as RuleMode[]).map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </TextField>
                {listed && (
                  <Autocomplete<Grape, true>
                    multiple sx={{ flexGrow: 1, minWidth: 260 }}
                    options={grapeOptions}
                    value={rule.grapes.map((id) => ({ _id: id, primaryName: nameOf(id) } as Grape))}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    getOptionLabel={(o) => o.primaryName}
                    onInputChange={(_, val) => searchGrapes(val)}
                    onChange={(_, val) => setColourRule(pIdx, colour, { grapes: val.map((g) => g._id) })}
                    renderInput={(params) => (
                      <TextField {...params} size="small" label="Permitted grapes" placeholder="type to search" />
                    )}
                  />
                )}
                {!listed && (
                  <Typography variant="caption" color="text.secondary" sx={{ pt: 1.5, flexGrow: 1 }}>
                    {MODE_HELP[rule.mode]}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ))}
    </Paper>
  );
};

export default RegionGrapeRulesPanel;
